import isEmpty from 'lodash/isEmpty'
import type { Address } from '@ztechapps/common.contract'
import { CountryList } from '../country-list'
import { UsStateList } from '../us-state-list'
import { buildAddress } from '../build-address'

/**
 * Parses a free-form address string into structured address components.
 * Uses regex-based pattern matching to extract street1, street2, city, state, zip, and country.
 * Supports both comma-separated and newline-separated address formats.
 *
 * @param fullAddress - The free-form address string to parse
 * @returns Partial Address object with extracted fields, or fallback with original in street1
 */
export function parseAddress(fullAddress: string): Partial<Address> {
  if (!fullAddress || typeof fullAddress !== 'string' || fullAddress.trim() === '') {
    return { street1: '', street2: '', city: '', state: '', zip: '', country: 'USA', fullAddress: '' }
  }

  const trimmed = fullAddress.trim()

  // Create lookup maps for faster access
  const countryByName: Record<string, string> = {}
  const countryByIso2: Record<string, string> = {}
  const countryByIso3: Record<string, string> = {}
  const stateByName: Record<string, string> = {}
  // Build a set of valid 2-letter US state codes for quick lookup
  const validUsStateCodes = new Set<string>()

  for (const country of CountryList) {
    countryByName[country.name.toLowerCase()] = country.value
    countryByIso2[country.iso2.toLowerCase()] = country.value
    countryByIso3[country.value.toLowerCase()] = country.value // Also map 3-letter code
  }

  for (const state of UsStateList) {
    const abbrev = state.value.toUpperCase()
    stateByName[state.name.toLowerCase()] = abbrev
    stateByName[abbrev.toLowerCase()] = abbrev
    validUsStateCodes.add(abbrev)
  }

  // Regex patterns
  const zipPattern = /\b(\d{5}(?:-\d{4})?)\b/
  // UK postal code pattern (alphanumeric with space in middle)
  const ukPostalCodePattern = /\b([A-Z]{1,2}[0-9][A-Z0-9]? [0-9][A-Z]{2})\b/i
  // Country pattern: look for country name or ISO code at the end
  const countryPattern = /,\s*([^,]+?)\s*$/i
  // Secondary address pattern - matches common apartment/suite/unit designations
  const secondaryPattern =
    /\b(Apt\.?|Suite|Unit|Ste\.?|Building|Bldg\.?|Floor|#|Room|Rm\.?|Dept\.?|Department)\s*([A-Za-z0-9]+(?:[ -][A-Za-z0-9]+)*)\b/i
  // Street address keywords that indicate this line contains a street address
  const streetKeywords =
    /(?<![a-zA-Z])\b(Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Drive|Dr|Lane|Ln|Way|Court|Ct|Circle|Cir|Place|Pl|Parkway|Pkwy|Highway|Hwy|Interstate|I-|Route|Rte)\b(?![a-zA-Z-])/i

  // Detect if input uses newlines as the primary separator
  const newlineCount = (trimmed.match(/\n/g) || []).length
  const commaCount = (trimmed.match(/,/g) || []).length
  const firstLine = trimmed.split('\n')[0]
  const firstLineHasComma = firstLine?.includes(',')
  const usesNewlines = newlineCount > 0 && (newlineCount > commaCount || !firstLineHasComma)
  const separator = usesNewlines ? '\n' : ','

  // Split by the detected separator
  let parts = trimmed.split(separator).map((p) => p.trim())

  // If using newlines, we may need to further split lines that contain commas
  if (usesNewlines) {
    const expandedParts: string[] = []
    for (const part of parts) {
      if (part.includes(',')) {
        expandedParts.push(...part.split(',').map((p) => p.trim()))
      } else {
        expandedParts.push(part)
      }
    }
    parts = expandedParts
  }

  let street1 = ''
  let street2 = ''
  let city = ''
  let state = ''
  let zip = ''
  let country = ''
  let company = ''
  let remainingParts = [...parts]

  // Check if first part might be a company name (no street keywords, doesn't start with digit)
  if (remainingParts.length >= 2) {
    const firstPart = remainingParts[0]
    const hasStreetKeywords = !!firstPart.match(streetKeywords)
    const startsWithDigit = !!firstPart.match(/^\d+/)
    const hasSecondary = !!firstPart.match(secondaryPattern)

    if (!hasStreetKeywords && !startsWithDigit && !hasSecondary) {
      company = firstPart
      remainingParts = remainingParts.slice(1)
    }
  }

  // Try to extract country from the end
  const lastPart = parts[parts.length - 1]
  const countryMatch = lastPart.match(countryPattern)

  if (countryMatch) {
    const potentialCountry = countryMatch[1].trim()
    const lowerCountry = potentialCountry.toLowerCase()

    if (countryByName[lowerCountry]) {
      country = countryByName[lowerCountry]
      remainingParts = remainingParts.slice(0, -1)
    } else if (countryByIso2[lowerCountry]) {
      country = countryByIso2[lowerCountry]
      remainingParts = remainingParts.slice(0, -1)
    } else if (countryByIso3[lowerCountry]) {
      country = countryByIso3[lowerCountry]
      remainingParts = remainingParts.slice(0, -1)
    }
  } else {
    // Also check if the last part itself is a country code
    const lastPartLower = lastPart.toLowerCase()
    if (countryByIso3[lastPartLower]) {
      country = countryByIso3[lastPartLower]
      remainingParts = remainingParts.slice(0, -1)
    } else if (countryByIso2[lastPartLower]) {
      country = countryByIso2[lastPartLower]
      remainingParts = remainingParts.slice(0, -1)
    } else if (countryByName[lastPartLower]) {
      country = countryByName[lastPartLower]
      remainingParts = remainingParts.slice(0, -1)
    }
  }

  // Check if we have UK postal code pattern (needs special handling)
  let hasUkPostalCode = false
  for (let i = remainingParts.length - 1; i >= Math.max(0, remainingParts.length - 3); i--) {
    const part = remainingParts[i]
    if (part.match(ukPostalCodePattern)) {
      zip = part
      remainingParts.splice(i, 1)
      hasUkPostalCode = true
      break
    }
  }

  // Try to extract ZIP code (5-digit or 5+4 format) - only if not already extracted as UK postal code
  // For non-US addresses, skip ZIP extraction
  if (!hasUkPostalCode && !isEmpty(remainingParts) && (country === 'USA' || !country)) {
    const lastPart = remainingParts[remainingParts.length - 1]
    const zipMatch = lastPart.match(zipPattern)

    if (zipMatch) {
      zip = zipMatch[1]
      const lastWithoutZip = lastPart
        .replace(zipPattern, '')
        .trim()
        .replace(/[,\s]+$/, '')
      if (lastWithoutZip) {
        remainingParts[remainingParts.length - 1] = lastWithoutZip
      } else {
        remainingParts.pop()
      }
    }
  }

  // After zip extraction and company check, check if the last part has "City ST" format
  // Only for US addresses
  if (!isEmpty(remainingParts) && (country === 'USA' || !country)) {
    const lastPart = remainingParts[remainingParts.length - 1]
    const cityStateMatch = lastPart.match(/^(.+?)\s+([A-Z]{2})$/)
    if (cityStateMatch) {
      const potentialState = cityStateMatch[2].toUpperCase()
      if (stateByName[potentialState.toLowerCase()]) {
        const potentialCity = cityStateMatch[1].trim()
        if (!potentialCity.match(streetKeywords) && !potentialCity.match(/^\d+/)) {
          city = potentialCity
          state = stateByName[potentialState.toLowerCase()]
          remainingParts.pop()
        }
      }
    }
  }

  // Try to extract state (2-letter abbreviation or full name) from remaining parts
  // Only for US addresses - for non-US, fallback to normal parsing
  if (!state && !isEmpty(remainingParts) && (country === 'USA' || !country)) {
    for (let i = remainingParts.length - 1; i >= Math.max(0, remainingParts.length - 2); i--) {
      const part = remainingParts[i]
      const stateMatch = part.match(/^([A-Za-z]{2})$/)

      if (stateMatch) {
        const potentialState = stateMatch[1].toUpperCase()
        if (stateByName[potentialState.toLowerCase()]) {
          state = stateByName[potentialState.toLowerCase()]
          remainingParts.splice(i, 1)
          break
        }
      }
    }
  }

  // If no state found by 2-letter match, try full state name in remaining parts
  // Only for US addresses
  if (!state && remainingParts.length >= 2 && (country === 'USA' || !country)) {
    const secondToLast = remainingParts[remainingParts.length - 1]
    for (const stateName of Object.keys(stateByName)) {
      if (secondToLast.toLowerCase() === stateName && stateName.length > 2) {
        state = stateByName[stateName]
        remainingParts.pop()
        break
      }
    }
  }

  // For non-US addresses, fall back to normal parsing without US state logic
  // Remaining parts should contain city and street info
  if (remainingParts.length >= 2) {
    const lastPart = remainingParts[remainingParts.length - 1]
    if (lastPart.match(secondaryPattern) && !city) {
      const secondToLast = remainingParts[remainingParts.length - 2]
      if (!validUsStateCodes.has(secondToLast.toUpperCase())) {
        city = secondToLast
        const streetParts = remainingParts.slice(0, -2)
        if (streetParts.length === 1) {
          street1 = streetParts[0]
        } else if (streetParts.length >= 2) {
          street1 = streetParts.join(' ')
        }
        street2 = lastPart
      } else {
        city = lastPart
        const streetParts = remainingParts.slice(0, -1)
        if (streetParts.length === 1) {
          street1 = streetParts[0]
        } else if (streetParts.length >= 2) {
          street1 = streetParts.join(' ')
        }
      }
    } else if (!city) {
      const potentialCity = lastPart
      // For US addresses, check if last part is a valid US state code
      // For non-US addresses, the format is typically: Street, City, Province, Country
      // So after removing country: ['Street', 'City', 'Province'] - city is second-to-last, province is last
      if (country && country !== 'USA') {
        // Non-US: second-to-last is city, last is province/state (if 3+ parts)
        // If only 2 parts: ['Street', 'City'] - last is city
        if (remainingParts.length >= 3) {
          // Format: Street, City, Province - city is second-to-last, province is last
          city = remainingParts[remainingParts.length - 2]
          state = remainingParts[remainingParts.length - 1]
          const streetParts = remainingParts.slice(0, -2)
          if (streetParts.length === 1) {
            street1 = streetParts[0]
          } else if (streetParts.length >= 2) {
            const combinedStreet = streetParts.join(' ')
            const secondaryMatch = combinedStreet.match(secondaryPattern)
            if (secondaryMatch) {
              const matchIndex = combinedStreet.toLowerCase().indexOf(secondaryMatch[0].toLowerCase())
              street2 = combinedStreet.substring(matchIndex)
              street1 = combinedStreet
                .substring(0, matchIndex)
                .trim()
                .replace(/[,\s]+$/, '')
            } else {
              street2 = streetParts[streetParts.length - 1]
              street1 = streetParts.slice(0, -1).join(' ')
            }
          }
        } else if (remainingParts.length === 2) {
          // Format: Street, City - last is city
          city = remainingParts[remainingParts.length - 1]
          street1 = remainingParts[0]
        } else if (remainingParts.length === 1) {
          city = remainingParts[0]
        }
      } else if (!validUsStateCodes.has(potentialCity.toUpperCase())) {
        // US address and last part is not a state code - it's the city
        city = lastPart
        const streetParts = remainingParts.slice(0, -1)
        if (streetParts.length === 1) {
          const singlePart = streetParts[0]
          const secondaryMatch = singlePart.match(secondaryPattern)
          if (secondaryMatch) {
            const matchIndex = singlePart.toLowerCase().indexOf(secondaryMatch[0].toLowerCase())
            street2 = singlePart.substring(matchIndex)
            street1 = singlePart
              .substring(0, matchIndex)
              .trim()
              .replace(/[,\s]+$/, '')
          } else {
            street1 = singlePart
          }
        } else if (streetParts.length >= 2) {
          const combinedStreet = streetParts.join(' ')
          const secondaryMatch = combinedStreet.match(secondaryPattern)

          if (secondaryMatch) {
            const matchIndex = combinedStreet.toLowerCase().indexOf(secondaryMatch[0].toLowerCase())
            street2 = combinedStreet.substring(matchIndex)
            street1 = combinedStreet
              .substring(0, matchIndex)
              .trim()
              .replace(/[,\s]+$/, '')
          } else {
            street2 = streetParts[streetParts.length - 1]
            street1 = streetParts.slice(0, -1).join(' ')
          }
        }
      } else {
        // Last part is a US state code, treat second-to-last as city
        city = remainingParts[remainingParts.length - 2]
        const streetParts = remainingParts.slice(0, -2)
        if (streetParts.length === 1) {
          street1 = streetParts[0]
        } else if (streetParts.length >= 2) {
          const lastStreetPart = streetParts[streetParts.length - 1]
          if (lastStreetPart.match(secondaryPattern)) {
            street2 = lastStreetPart
            street1 = streetParts.slice(0, -1).join(' ')
          } else {
            street2 = lastStreetPart
            street1 = streetParts.slice(0, -1).join(' ')
          }
        }
      }
    } else {
      const streetParts = remainingParts
      if (streetParts.length === 1) {
        street1 = streetParts[0]
      } else if (streetParts.length >= 2) {
        for (let i = streetParts.length - 1; i >= 0; i--) {
          if (streetParts[i].match(secondaryPattern)) {
            street2 = streetParts[i]
            street1 = streetParts.slice(0, i).join(' ')
            break
          }
        }
        if (!street2) {
          street2 = streetParts[streetParts.length - 1]
          street1 = streetParts.slice(0, -1).join(' ')
        }
      }
    }
  } else if (remainingParts.length === 1 && !city) {
    const singlePart = remainingParts[0]
    const secondaryMatch = singlePart.match(secondaryPattern)
    if (secondaryMatch) {
      const matchIndex = singlePart.toLowerCase().indexOf(secondaryMatch[0].toLowerCase())
      street2 = singlePart.substring(matchIndex)
      street1 = singlePart
        .substring(0, matchIndex)
        .trim()
        .replace(/[,\s]+$/, '')
    } else {
      street1 = singlePart
    }
  }

  // For multi-line addresses with company name, ensure proper parsing
  if (company && city === '' && remainingParts.length >= 1) {
    const lastPart = remainingParts[remainingParts.length - 1]
    const cityStateMatch = lastPart.match(/^(.+?)\s+([A-Z]{2})$/)
    if (cityStateMatch) {
      const potentialState = cityStateMatch[2].toUpperCase()
      if (stateByName[potentialState.toLowerCase()]) {
        city = cityStateMatch[1].trim()
        state = stateByName[potentialState.toLowerCase()]
      }
    }
    const zipMatch = lastPart.match(zipPattern)
    if (zipMatch && city === '') {
      const zipWithoutNumber = lastPart
        .replace(zipPattern, '')
        .trim()
        .replace(/[,\s]+$/, '')
      if (zipWithoutNumber) {
        const stateMatch = zipWithoutNumber.match(/^([A-Za-z]{2})$/)
        if (stateMatch) {
          const potentialState = stateMatch[1].toUpperCase()
          if (stateByName[potentialState.toLowerCase()]) {
            state = stateByName[potentialState.toLowerCase()]
            city = ''
          }
        }
        const cityStateMatch = zipWithoutNumber.match(/^(.+?)\s+([A-Z]{2})$/)
        if (cityStateMatch) {
          const potentialState = cityStateMatch[2].toUpperCase()
          if (stateByName[potentialState.toLowerCase()]) {
            city = cityStateMatch[1].trim()
            state = stateByName[potentialState.toLowerCase()]
          }
        }
      }
    }
  }

  // If we couldn't parse properly, fall back to original input
  if (!city && !state && !zip && !country && !isEmpty(remainingParts)) {
    return { street1: trimmed, street2: '', city: '', state: '', zip: '', country: 'USA', fullAddress: trimmed }
  }

  return buildAddress({
    street1: street1 || trimmed,
    street2,
    city,
    state,
    zip,
    country: country || 'USA',
    fullAddress: trimmed,
    company,
  })
}
